import { KeyboardEvent, useState } from 'react';
import { Dropdown } from './Dropdown';

interface DropdownSelectProps<T> {
  value?: T | null;
  onChange: (value: string, item?: T) => void;
  options: T[];
  getOptionLabel: (item: T) => string;
  getOptionValue: (item: T) => string | number;
  placeholder?: string;
  className?: string;
  showDropdownIcon?: boolean;
  error?: boolean;
  disabled?: boolean;
}

export function DropdownSelect<T>({
  value,
  onChange,
  options,
  getOptionLabel,
  getOptionValue,
  placeholder,
  className = '',
  showDropdownIcon = true,
  error = false,
  disabled = false,
}: DropdownSelectProps<T>) {
  // Index into `visibleOptions` of the keyboard-highlighted option (-1 = none).
  const [activeIndex, setActiveIndex] = useState(-1);

  const visibleOptions = options.filter(
    item => !value || getOptionValue(item) !== getOptionValue(value)
  );

  const selectOption = (item: T) => {
    onChange(getOptionLabel(item), item);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled || visibleOptions.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, visibleOptions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex(prev => (prev <= 0 ? 0 : prev - 1));
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < visibleOptions.length) {
          event.preventDefault();
          selectOption(visibleOptions[activeIndex]);
          // Blurring closes the option list while committing the selection.
          event.currentTarget.blur();
        }
        break;
      case 'Escape':
        setActiveIndex(-1);
        event.currentTarget.blur();
        break;
      default:
        break;
    }
  };

  return (
    <Dropdown
      value={value ? getOptionLabel(value) : ''}
      onChange={() => {}} // Read-only for dropdown
      placeholder={placeholder}
      className={className}
      showDropdownIcon={showDropdownIcon}
      error={error}
      disabled={disabled}
      readOnly={true}
      onKeyDown={handleKeyDown}
      onBlur={() => setActiveIndex(-1)}
    >
      {visibleOptions.map((item, index) => (
        <li
          key={getOptionValue(item)}
          role="option"
          aria-selected={index === activeIndex}
          className={`font-ambit cursor-pointer border-b border-primary/10 px-4 py-2.5 text-[0.9375rem] font-normal not-italic leading-[1.25rem] text-secondary transition-colors last:border-b-0 hover:bg-primary/5 hover:text-primary ${
            index === activeIndex ? 'bg-primary/5 text-primary' : ''
          }`}
          onMouseEnter={() => setActiveIndex(index)}
          onMouseDown={() => {
            selectOption(item);
          }}
        >
          {getOptionLabel(item)}
        </li>
      ))}
    </Dropdown>
  );
}
